import type { ColumnDef } from "../entities/types";
import { measureAlias } from "./measure-alias";
import type { GroupBy, Measure, StructuredQueryConfig } from "./types";

export type ChartKind = "bar" | "line" | "donut" | "kpi" | null;

export type ChartOverride = ChartKind | "auto";

export const REPORT_MAX_ROWS = 10_000;

export type ChartSeriesPlan = {
  kind: Exclude<ChartKind, "kpi" | null>;
  xKey: string;
  yKey: string;
  seriesKey?: string;
  direction?: "horizontal" | "vertical";
};

export function isReportQuery(
  config: Pick<StructuredQueryConfig, "groupBy" | "measures">,
): boolean {
  return config.groupBy.length > 0;
}

export function deriveChartKind(
  config: Pick<StructuredQueryConfig, "groupBy" | "measures">,
  columns: readonly ColumnDef[],
  rowCount: number,
  override: ChartOverride = "auto",
): ChartKind {
  if (override !== "auto" && override !== null) {
    return override;
  }

  const { groupBy, measures } = config;
  if (groupBy.length === 0 || measures.length === 0) {
    return null;
  }

  const columnMap = new Map(columns.map((c) => [c.field, c]));
  const numericMeasures = measures.filter((m) =>
    isNumericMeasure(m, columnMap),
  );

  if (numericMeasures.length !== 1) {
    return null;
  }

  if (rowCount === 1) {
    return "kpi";
  }

  if (groupBy.length > 2) {
    return null;
  }

  const temporal = groupBy.filter((gb) =>
    isTemporalGroup(gb, columnMap.get(gb.field)),
  );
  const categorical = groupBy.filter(
    (gb) => !isTemporalGroup(gb, columnMap.get(gb.field)),
  );

  if (
    groupBy.length === 2 &&
    temporal.length === 1 &&
    categorical.length === 1
  ) {
    return "line";
  }

  if (groupBy.length === 1) {
    const gb = groupBy[0];
    if (isTemporalGroup(gb, columnMap.get(gb.field))) {
      return "line";
    }
    return "bar";
  }

  return null;
}

export function buildChartSeriesPlan(
  config: Pick<StructuredQueryConfig, "groupBy" | "measures">,
  columns: readonly ColumnDef[],
  rowCount: number,
  override: ChartOverride = "auto",
): ChartSeriesPlan | null {
  const kind = deriveChartKind(config, columns, rowCount, override);
  if (kind === null || kind === "kpi") {
    return null;
  }

  const columnMap = new Map(columns.map((c) => [c.field, c]));
  const measure = config.measures.find((m) => isNumericMeasure(m, columnMap));
  if (!measure) {
    return null;
  }

  const yKey = measureAlias(measure);
  const temporal = config.groupBy.filter((gb) =>
    isTemporalGroup(gb, columnMap.get(gb.field)),
  );
  const categorical = config.groupBy.filter(
    (gb) => !isTemporalGroup(gb, columnMap.get(gb.field)),
  );

  if (kind === "line") {
    if (config.groupBy.length === 1 && temporal.length === 1) {
      return {
        kind: "line",
        xKey: temporal[0].field,
        yKey,
      };
    }
    if (
      config.groupBy.length === 2 &&
      temporal.length === 1 &&
      categorical.length === 1
    ) {
      return {
        kind: "line",
        xKey: temporal[0].field,
        yKey,
        seriesKey: categorical[0].field,
      };
    }
    return null;
  }

  if (kind === "donut") {
    const category = categorical[0] ?? config.groupBy[0];
    return {
      kind: "donut",
      xKey: category.field,
      yKey,
    };
  }

  const category = categorical[0] ?? config.groupBy[0];
  return {
    kind: "bar",
    xKey: category.field,
    yKey,
    direction: "vertical",
  };
}

export const MULTI_SERIES_LINE_MAX_SERIES = 8;

export type MultiSeriesLineChartRow = Record<string, unknown> & {
  __categoryLabel: string;
};

/** Line charts must plot points in x-axis order, not query default (measure DESC). */
export function sortRowsForChart(
  rows: readonly Record<string, unknown>[],
  plan: ChartSeriesPlan,
): Record<string, unknown>[] {
  if (plan.kind !== "line") {
    return [...rows];
  }

  return [...rows].sort((left, right) =>
    compareChartAxisValue(left[plan.xKey], right[plan.xKey]),
  );
}

export function pivotMultiSeriesLineRows(
  rows: readonly Record<string, unknown>[],
  plan: ChartSeriesPlan & { seriesKey: string },
  labels: ReadonlyMap<string, string>,
  options?: {
    maxPoints?: number;
    maxSeries?: number;
    formatXLabel?: (value: unknown, field: string) => string;
    formatSeriesName?: (value: unknown, field: string) => string;
  },
): {
  rows: MultiSeriesLineChartRow[];
  seriesNames: string[];
  totalSeriesCount: number;
  pointsTruncated: boolean;
  seriesTruncated: boolean;
} {
  const maxPoints = options?.maxPoints ?? Number.POSITIVE_INFINITY;
  const maxSeries = options?.maxSeries ?? MULTI_SERIES_LINE_MAX_SERIES;
  const formatXLabel =
    options?.formatXLabel ??
    ((value, field) => formatChartCategoryLabel(value, field, labels));
  const formatSeriesName =
    options?.formatSeriesName ??
    ((value, field) => formatChartCategoryLabel(value, field, labels));
  const sortedRows = sortRowsForChart(rows, plan);

  const seriesTotals = new Map<string, number>();
  for (const row of sortedRows) {
    const seriesName = formatSeriesName(row[plan.seriesKey], plan.seriesKey);
    const value = coerceChartNumber(row[plan.yKey]) ?? 0;
    seriesTotals.set(seriesName, (seriesTotals.get(seriesName) ?? 0) + value);
  }

  const rankedSeries = [...seriesTotals.entries()]
    .sort(
      (left, right) => right[1] - left[1] || left[0].localeCompare(right[0]),
    )
    .map(([name]) => name);
  const seriesTruncated = rankedSeries.length > maxSeries;
  const seriesNames = rankedSeries.slice(0, maxSeries);
  const seriesNameSet = new Set(seriesNames);

  const pointsByX = new Map<string, MultiSeriesLineChartRow>();
  const xOrder: string[] = [];
  let skippedPoints = false;

  for (const row of sortedRows) {
    const xLabel = formatXLabel(row[plan.xKey], plan.xKey);
    if (!pointsByX.has(xLabel)) {
      if (xOrder.length >= maxPoints) {
        skippedPoints = true;
        continue;
      }
      xOrder.push(xLabel);
      pointsByX.set(xLabel, { __categoryLabel: xLabel });
    }

    const seriesName = formatSeriesName(row[plan.seriesKey], plan.seriesKey);
    if (!seriesNameSet.has(seriesName)) {
      continue;
    }

    const point = pointsByX.get(xLabel);
    if (!point) {
      continue;
    }
    point[seriesName] = coerceChartNumber(row[plan.yKey]);
  }

  return {
    rows: xOrder.map(
      (xLabel) => pointsByX.get(xLabel) ?? { __categoryLabel: xLabel },
    ),
    seriesNames,
    totalSeriesCount: rankedSeries.length,
    pointsTruncated: skippedPoints,
    seriesTruncated,
  };
}

export function coerceChartNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function formatChartCategoryLabel(
  value: unknown,
  field: string,
  labels: ReadonlyMap<string, string>,
): string {
  if (value == null || value === "") {
    return "—";
  }
  if (value instanceof Date) {
    return value.toISOString().slice(0, 10);
  }
  if (typeof value === "string") {
    return value;
  }
  if (typeof value === "number" || typeof value === "boolean") {
    if (typeof value === "boolean") {
      return value ? "Yes" : "No";
    }
    return String(value);
  }
  return labels.get(field) ?? String(value);
}

function compareChartAxisValue(left: unknown, right: unknown): number {
  const leftTime = toSortableTime(left);
  const rightTime = toSortableTime(right);
  if (leftTime != null && rightTime != null) {
    return leftTime - rightTime;
  }
  return String(left ?? "").localeCompare(String(right ?? ""));
}

function toSortableTime(value: unknown): number | null {
  if (value instanceof Date) {
    return value.getTime();
  }
  if (typeof value === "string" || typeof value === "number") {
    const parsed = Date.parse(String(value));
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

export function readKpiValue(
  rows: readonly Record<string, unknown>[],
  config: Pick<StructuredQueryConfig, "measures">,
): { label: string; value: number } | null {
  if (rows.length !== 1 || config.measures.length !== 1) {
    return null;
  }
  const measure = config.measures[0];
  const key = measureAlias(measure);
  const raw = rows[0][key];
  const value = typeof raw === "number" ? raw : Number(raw);
  if (!Number.isFinite(value)) {
    return null;
  }
  return { label: key, value };
}

function isTemporalGroup(gb: GroupBy, def: ColumnDef | undefined): boolean {
  if (gb.bucket) return true;
  return def?.type === "timestamp" || def?.type === "date";
}

function isNumericMeasure(
  measure: Measure,
  columnMap: Map<string, ColumnDef>,
): boolean {
  if (measure.field === "*") {
    return measure.func === "count" || measure.func === "count_distinct";
  }
  if (measure.func === "count" || measure.func === "count_distinct") {
    return true;
  }
  const def = columnMap.get(measure.field);
  return def?.type === "number";
}
